import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import { jwtDecode } from 'jwt-decode';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import UpgradePrompt from './UpgradePrompt';
import './VideoConference.css';

const HostConference = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPro, setIsPro] = useState(null); // null = loading
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleNotes, setScheduleNotes] = useState('');
  const navigate = useNavigate();

  // Check subscription status
  useEffect(() => {
    const checkPlan = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/billing/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIsPro(['pro','circle_member','circle_3','circle_5','circle_10'].includes(response.data.plan?.toLowerCase()), 'circle_3','circle_5', 'circle_10');
      } catch (error) {
        console.error('Error checking plan:', error);
        setIsPro(false);
      }
    };
    checkPlan();
  }, []);

  const handleScheduleCall = async () => {
    if (!scheduleDate) {
      alert('Please select a date and time.');
      return;
    }
  
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    if (!token || !userId) {
      alert('Authentication error. Please log in again.');
      navigate('/login');
      return;
    }
  
    const roomId = uuidv4();
    const hostName = localStorage.getItem('username') || 'Host';
    const hostLink = `/host-conference?room=${roomId}`;
    const guestLink = `${window.location.origin}/guest-conference?room=${roomId}`;
  
    const newCall = {
      room: roomId,
      hostName,
      startTime: scheduleDate.toISOString(),
      endTime: new Date(scheduleDate.getTime() + 60 * 60 * 1000).toISOString(),
      notes: scheduleNotes,
      hostLink,
      guestLink,
    };
  
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/users/${userId}/schedule-call`, newCall, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      if (response.status === 201) {
        setScheduledCalls((prev) => [...prev, response.data.scheduledCall]);
        alert('Call scheduled successfully!');
      }
    } catch (error) {
      console.error('Error scheduling call:', error.response?.data || error.message);
      alert('Failed to schedule call. Please try again.');
    }
  };
  

  useEffect(() => {
    const fetchScheduledCalls = async () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
  
      if (!token || !userId) {
        navigate('/login');
        return;
      }
  
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/${userId}/scheduled-calls`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
  
        if (response.status === 200) {
          console.log('Fetched scheduled calls:', response.data.scheduledCalls);
          setScheduledCalls(response.data.scheduledCalls); // Use the fetched data
        } else {
          console.error('Failed to fetch scheduled calls:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching scheduled calls:', error.response?.data || error.message);
      }
    };
  
    fetchScheduledCalls();
  }, [navigate]);

  useEffect(() => {
    const fetchScheduledCalls = async () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
  
      if (!token || !userId) {
        navigate('/login');
        return;
      }
  
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/${userId}/events`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
  
        if (response.status === 200) {
          const videoConferenceEvents = response.data.filter(
            (event) => event.addVideoConference // Only include events with video conferences
          );
          setScheduledCalls(videoConferenceEvents);
        } else {
          console.error('Failed to fetch events:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching events:', error.response?.data || error.message);
      }
    };
  
    fetchScheduledCalls();
  }, [navigate]);

  useEffect(() => {
    const fetchEventsWithVideoConferences = async () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
  
      if (!token || !userId) {
        navigate('/login');
        return;
      }
  
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/${userId}/events`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
  
        if (response.status === 200) {
          const videoConferenceEvents = response.data.filter(
            (event) => event.addVideoConference // Only include events with video conferences
          );
  
          // Format events to match `scheduledCalls` structure
          const formattedEvents = videoConferenceEvents.map((event) => ({
            room: event._id,
            hostName: 'Host', // Replace with event-specific host name if available
            startTime: event.startTime,
            endTime: event.endTime,
            notes: event.description, // Use event description as notes
            hostLink: event.hostLink,
            guestLink: event.guestLink,
          }));
  
          // Merge with manually scheduled calls
          setScheduledCalls((prev) => [...prev, ...formattedEvents]);
        } else {
          console.error('Failed to fetch events:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching events:', error.response?.data || error.message);
      }
    };
  
    fetchEventsWithVideoConferences();
  }, [navigate]);
  

  const copyGuestLink = (link) => {
    navigator.clipboard.writeText(link).then(() => {
      alert('Guest link copied to clipboard!');
    });
  };

  const startVideoCall = (hostLink) => {
    navigate(hostLink);
  };

  const checkAuthentication = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decoded.exp < currentTime) {
        navigate('/login');
        return;
      }

      setIsAuthenticated(true);
    } catch (error) {
      console.error('Invalid token:', error);
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    checkAuthentication();

    // Load existing scheduled calls from localStorage
    const savedCalls = JSON.parse(localStorage.getItem('scheduledCalls')) || [];
    setScheduledCalls(savedCalls);
  }, [checkAuthentication]);

  const deleteScheduledCall = async (callId) => {
    console.log('Attempting to delete call with ID:', callId); // Debugging
  
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
  
    if (!token || !userId) {
      alert('Authentication error. Please log in again.');
      navigate('/login');
      return;
    }
  
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/users/${userId}/scheduled-calls/${callId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      if (response.status === 200) {
        setScheduledCalls((prevCalls) => prevCalls.filter((call) => call._id !== callId));
        alert('Scheduled call deleted successfully!');
      } else {
        alert('Failed to delete scheduled call');
      }
    } catch (error) {
      console.error('Error deleting scheduled call:', error.response?.data || error.message);
      alert('Failed to delete scheduled call. Please try again.');
    }
  };
  

  if (!isAuthenticated) {
    return <div>Loading...</div>;
  }

  if (isPro === null) {
    return <div>Loading...</div>;
  }

  if (isPro === false) {
    return <UpgradePrompt feature="Video Conferencing" />;
  }

  return (
    <div className="host-conference-container">
      <h1>1:1 Video Conference Scheduler</h1>

      {/* Schedule a Call Section */}
      <div className="schedule-section">
        <h2>Schedule a Call</h2>
        <div className="schedule-inputs">
          <DatePicker
            selected={scheduleDate}
            onChange={(date) => setScheduleDate(date)}
            showTimeSelect
            dateFormat="Pp"
            placeholderText="Select Date and Time"
            className="schedule-datepicker"
          />
          <textarea
            value={scheduleNotes}
            onChange={(e) => setScheduleNotes(e.target.value)}
            placeholder="Add notes (optional)"
            className="schedule-notes"
          />
          <button onClick={handleScheduleCall} className="schedule-button">
            Schedule Call
          </button>
        </div>
      </div>

{/* Scheduled Calls Section */}
<div className="scheduled-calls-section">
  <h2>Scheduled Calls</h2>
  {scheduledCalls.length > 0 ? (
    <ul className="scheduled-calls-list">
{scheduledCalls.map((call, index) => (
  <li key={index} className="scheduled-call-item">
    <div>
      <div className="call-info">
        <strong>Room:</strong> {call.room} <br />
        <strong>Date:</strong> {new Date(call.startTime).toLocaleString()} <br />
        {call.notes && (
          <>
            <strong>Notes:</strong> {call.notes} <br />
          </>
        )}
      </div>
      <div className="call-actions">
        <button
          onClick={() => startVideoCall(call.hostLink)}
          className="start-call-button"
        >
          Enter Room
        </button>
        <button
          onClick={() => copyGuestLink(call.guestLink)}
          className="copy-link-button"
        >
          Copy Guest Link
        </button>
        <button
          onClick={() => deleteScheduledCall(call._id)}
          className="delete-call-button"
        >
          Delete
        </button>
      </div>
    </div>
  </li>
))}
    </ul>
  ) : (
    <p>No scheduled calls yet.</p>
  )}
</div>

      {/* Navigation Buttons */}
      <div className="navigation-buttons">
        <button className="navigation-btn" onClick={() => navigate(-1)}>
          Go Back
        </button>
        <button className="navigation-btn" onClick={() => navigate('/dashboard')}>
          Dashboard
        </button>
      </div>
    </div>
  );
};

export default HostConference;